/**
 * B2ExecutionTemplateEditor.jsx - Sprint 2
 * 
 * Constructor dinámico de formularios para deliverables (B2).
 * COMPONENTE OBLIGATORIO - El curso no puede publicarse sin template B2.
 * 
 * Field types: text, textarea, number, date, select, file
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md
 */

import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  GripVertical,
  Type,
  AlignLeft,
  Hash,
  Calendar,
  ListChecks,
  FileUp,
  X,
  Save,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';

const B2ExecutionTemplateEditor = ({ template = { instructions: '', fields: [] }, onChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    fieldId: '',
    type: 'text',
    label: '',
    placeholder: '',
    required: false,
    validation: {},
    options: []
  });
  const [errors, setErrors] = useState({});

  const fieldTypes = [
    { value: 'text', label: 'Texto corto', icon: Type, desc: 'Línea de texto' },
    { value: 'textarea', label: 'Texto largo', icon: AlignLeft, desc: 'Párrafo o ensayo' },
    { value: 'number', label: 'Número', icon: Hash, desc: 'Valor numérico' },
    { value: 'date', label: 'Fecha', icon: Calendar, desc: 'Selector de fecha' },
    { value: 'select', label: 'Selección', icon: ListChecks, desc: 'Opciones predefinidas' },
    { value: 'file', label: 'Archivo', icon: FileUp, desc: 'Subir archivo' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.label.trim()) {
      newErrors.label = 'La etiqueta es obligatoria';
    }
    
    if (!formData.fieldId.trim()) {
      // Auto-generar fieldId si no existe
      const autoFieldId = formData.label.toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
      setFormData(prev => ({ ...prev, fieldId: autoFieldId }));
    }
    
    if (formData.type === 'select' && formData.options.length === 0) {
      newErrors.options = 'Debes agregar al menos una opción';
    }
    
    return newErrors;
  };

  const handleAdd = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    const newFields = [...template.fields, { ...formData }];
    onChange({ ...template, fields: newFields });
    resetForm();
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setFormData({ ...template.fields[index] });
    setErrors({});
  };

  const handleUpdate = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    const newFields = [...template.fields];
    newFields[editingIndex] = formData;
    onChange({ ...template, fields: newFields });
    resetForm();
  };

  const handleDelete = (index) => {
    if (confirm('¿Eliminar este campo?')) {
      const newFields = template.fields.filter((_, i) => i !== index);
      onChange({ ...template, fields: newFields });
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newFields = [...template.fields];
    [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
    onChange({ ...template, fields: newFields });
  };

  const handleMoveDown = (index) => {
    if (index === template.fields.length - 1) return;
    const newFields = [...template.fields];
    [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    onChange({ ...template, fields: newFields });
  };

  const resetForm = () => {
    setFormData({
      fieldId: '',
      type: 'text',
      label: '',
      placeholder: '',
      required: false,
      validation: {},
      options: []
    });
    setIsAdding(false);
    setEditingIndex(null);
    setErrors({});
  };

  const addOption = () => {
    const newOption = prompt('Ingresa el texto de la opción:');
    if (newOption && newOption.trim()) {
      setFormData({
        ...formData,
        options: [...formData.options, newOption.trim()]
      });
    }
  };

  const removeOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    });
  };

  const getFieldIcon = (type) => {
    const fieldType = fieldTypes.find(ft => ft.value === type);
    return fieldType?.icon || Type;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            Template de Ejecución (B2)
            <span className="text-red-500">*</span>
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Diseña el formulario que los equipos completarán cada semana
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2 transition-colors"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showPreview ? 'Editar' : 'Preview'}
          </button>
          {!isAdding && editingIndex === null && !showPreview && (
            <button
              onClick={() => setIsAdding(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Agregar Campo
            </button>
          )}
        </div>
      </div>

      {/* Instrucciones */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Instrucciones del Template
        </label>
        <textarea
          value={template.instructions}
          onChange={(e) => onChange({ ...template, instructions: e.target.value })}
          placeholder="Describe qué deben hacer los equipos en este deliverable..."
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Preview Mode */}
      {showPreview && (
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Vista Previa del Formulario</h4>
          
          {template.instructions && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900">{template.instructions}</p>
            </div>
          )}
          
          {template.fields.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Sin campos aún</p>
          ) : (
            <div className="space-y-4">
              {template.fields.map((field, index) => {
                const Icon = getFieldIcon(field.type);
                return (
                  <div key={index}>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {field.label}
                      {field.required && <span className="text-red-500">*</span>}
                    </label>
                    
                    {field.type === 'text' && (
                      <input
                        type="text"
                        placeholder={field.placeholder}
                        disabled
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                    )}
                    
                    {field.type === 'textarea' && (
                      <textarea
                        placeholder={field.placeholder}
                        rows={4}
                        disabled
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 resize-none"
                      />
                    )}
                    
                    {field.type === 'number' && (
                      <input
                        type="number"
                        placeholder={field.placeholder}
                        disabled
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                    )}
                    
                    {field.type === 'date' && (
                      <input
                        type="date"
                        disabled
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                    )}
                    
                    {field.type === 'select' && (
                      <select
                        disabled
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      >
                        <option>Selecciona una opción...</option>
                        {field.options.map((opt, i) => (
                          <option key={i}>{opt}</option>
                        ))}
                      </select>
                    )}
                    
                    {field.type === 'file' && (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
                        <FileUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Arrastra un archivo o haz clic para seleccionar</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Lista de campos */}
      {!showPreview && template.fields.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">
            Campos del Formulario ({template.fields.length})
          </h4>
          {template.fields.map((field, index) => {
            const Icon = getFieldIcon(field.type);
            
            return (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === template.fields.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-3 bg-green-50 rounded-lg">
                  <Icon className="w-6 h-6 text-green-600" />
                </div>

                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    {field.label}
                    {field.required && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                        Requerido
                      </span>
                    )}
                  </h4>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                    <span className="capitalize">
                      {fieldTypes.find(ft => ft.value === field.type)?.label}
                    </span>
                    {field.placeholder && <span>"{field.placeholder}"</span>}
                    {field.type === 'select' && (
                      <span>{field.options.length} opciones</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(index)}
                    className="text-gray-400 hover:text-green-600 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Formulario de agregar/editar */}
      {(isAdding || editingIndex !== null) && !showPreview && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">
              {editingIndex !== null ? 'Editar Campo' : 'Nuevo Campo'}
            </h4>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Tipo de campo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Campo
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {fieldTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setFormData({ ...formData, type: type.value, options: [] })}
                      className={`p-3 border-2 rounded-lg flex flex-col items-start gap-1 transition-all ${
                        formData.type === type.value
                          ? 'border-green-500 bg-white'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${formData.type === type.value ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="text-sm font-medium text-gray-900">{type.label}</span>
                      <span className="text-xs text-gray-500">{type.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Label */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Etiqueta del Campo *
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Ej: Descripción del problema identificado"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              {errors.label && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.label}
                </p>
              )}
            </div>

            {/* Field ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID del Campo (opcional)
              </label>
              <input
                type="text"
                value={formData.fieldId}
                onChange={(e) => setFormData({ ...formData, fieldId: e.target.value })}
                placeholder="Se generará automáticamente si se deja vacío"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Identificador único para este campo (letras, números, guiones bajos)
              </p>
            </div>

            {/* Placeholder */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Placeholder
              </label>
              <input
                type="text"
                value={formData.placeholder}
                onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                placeholder="Texto de ayuda dentro del campo..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Opciones (solo para select) */}
            {formData.type === 'select' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opciones de Selección *
                </label>
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={option}
                        disabled
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                      <button
                        onClick={() => removeOption(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addOption}
                    className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-500 hover:text-green-600 transition-colors"
                  >
                    + Agregar Opción
                  </button>
                </div>
                {errors.options && (
                  <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.options}
                  </p>
                )}
              </div>
            )}

            {/* Required */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="required"
                checked={formData.required}
                onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <label htmlFor="required" className="text-sm text-gray-700">
                Campo obligatorio
              </label>
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4 border-t border-green-200">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 hover:bg-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={editingIndex !== null ? handleUpdate : handleAdd}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingIndex !== null ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {template.fields.length === 0 && !isAdding && editingIndex === null && !showPreview && (
        <div className="text-center py-12 bg-red-50 rounded-lg border-2 border-dashed border-red-300">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Template sin campos (⚠️ Obligatorio)
          </h3>
          <p className="text-gray-600 mb-4">
            El template B2 es obligatorio para publicar el curso.<br />
            Agrega al menos un campo al formulario.
          </p>
          <button
            onClick={() => setIsAdding(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Agregar Primer Campo
          </button>
        </div>
      )}

      {/* Info box */}
      {template.fields.length > 0 && !isAdding && editingIndex === null && !showPreview && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            ✅ El template tiene <strong>{template.fields.length} campo(s)</strong> configurado(s).
            Los equipos completarán este formulario como su deliverable semanal.
          </p>
        </div>
      )}
    </div>
  );
};

export default B2ExecutionTemplateEditor;
